import type { Register } from "@/server-types";
import { sentryLog } from "@/utils/sentry-utils";

const STORAGE_KEY = "onboarding-applied-promo-code";

export type AppliedPromoCode = NonNullable<Register["response"]["promoCode"]>;

export function parseAppliedPromoCode(
  raw: string | null,
): AppliedPromoCode | null {
  if (!raw) return null;

  try {
    const value: unknown = JSON.parse(raw);

    if (typeof value !== "object" || value === null) return null;

    const { code, discountPercent, discountMonths } = value as Record<
      string,
      unknown
    >;

    const isValid =
      typeof code === "string" &&
      code.length > 0 &&
      typeof discountPercent === "number" &&
      Number.isFinite(discountPercent) &&
      typeof discountMonths === "number" &&
      Number.isFinite(discountMonths);

    return isValid ? { code, discountPercent, discountMonths } : null;
  } catch {
    return null;
  }
}

/**
 * Registration reloads the page into the dashboard, so the discount it applied
 * is carried across in session storage for the wizard to confirm. A sign-up
 * without a code clears it, so an earlier sign-up in the tab cannot leak in.
 */
export function rememberAppliedPromoCode(
  promoCode: AppliedPromoCode | null,
): void {
  try {
    if (promoCode) {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(promoCode));
    } else {
      sessionStorage.removeItem(STORAGE_KEY);
    }
  } catch (error) {
    sentryLog.warn("Failed to remember the applied promo code", { error });
  }
}

export function readAppliedPromoCode(): AppliedPromoCode | null {
  try {
    return parseAppliedPromoCode(sessionStorage.getItem(STORAGE_KEY));
  } catch (error) {
    sentryLog.warn("Failed to read the applied promo code", { error });
    return null;
  }
}
